    <template>
        <div class="sw-modal edit-form-component">
            <div v-click-outside="close" class="sw-modal-content">
                <div class="row sw-modal-heading">
                    <div class="col"><h4><i class="fas fa-file-image"></i>Files</h4></div>
                </div>
                <div class="sw-modal-body">
                    <div v-if="!taggingFile" class="row question">
                        <div class="col-12 question__question">
                            Uploading files
                        </div>
                        <div class="col-12 text-note">
                            Please upload high resolution images in JPG, GIF, PNG or WEBP format (bigger than 2MB but
                            less than 20MB) or documents such as PDF files.
                        </div>
                        <div class="col-12">
                            <div class="edit-establishment__images-dropbox">
                                <form enctype="multipart/form-data" novalidate v-if="isInitial || isSaving">
                                <input type="file" multiple
                                       v-bind:name="uploadFieldName"
                                       v-bind:disabled="isSaving"
                                       v-on:change="filesChange($event.target.name, $event.target.files); fileCount = $event.target.files.length"
                                       accept="image/*"
                                       class="edit-establishment__images-input-file">
                                <p v-if="isInitial">
                                    Drag your file(s) here to upload<br> or click to browse
                                </p>
                                <p v-if="isSaving">
                                    Uploading {{ fileCount }} files...
                                </p>
                                </form>
                                <div class="row edit-establishment__images-feedback" v-if="isSuccess || isFailed">
                                    <!--SUCCESS-->
                                    <div v-if="isSuccess" class="col-12 alert alert-success" role="alert">
                                        Uploaded {{ fileCount }} file(s) successfully
                                    </div>
                                    <!--FAILED-->
                                    <div v-if="isFailed" class="col-12 alert alert-danger" role="alert">
                                        Uploaded failed
                                        <pre>{{ uploadError }}</pre>
                                        <a href="javascript:void(0)" @click.stop="reset()">Try again</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12">
                            <div v-for="(file, index) in copy.preferences.files"
                                 v-bind:key="index"
                                 class="edit-establishment__images-thumbnail edit-establishment__images-thumbnail--file">
                                <template v-if="isDeletingFile(file.reference)">
                                    <div class="edit-establishment__images-thumbnail--deleting">
                                        <Spinner/>
                                    </div>
                                </template>
                                <template v-else>
                                    <span v-on:click.stop="editFileTag(file)"
                                        v-bind:class="{'edit-establishment__images-thumbnail__tag--set' : file.roomType}"
                                        class="edit-establishment__images-thumbnail__tag">
                                        <i class="fas fa-tag"></i>
                                    </span>
                                    <img v-if="isImage(file)" v-bind:src="file.url"/>
                                    <div v-else class="edit-establishment__images-thumbnail__doc">
                                        <i class="fas fa-file-image"></i>
                                    </div>
                                    <div v-if="!isImage(file)" class="edit-establishment__images-thumbnail-filename">
                                        <div>{{getMediaFileNameFromURL(file.url)}}</div>
                                    </div>
                                    <i v-on:click.stop="copyURL(file, $event)" class="fas fa-copy edit-establishment__images-thumbnail-copy"></i>
                                    <i v-on:click.stop="deleteFile(file.reference)" class="fas fa-trash-alt edit-establishment__images-thumbnail-delete"></i>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div v-else class="row question edit-establishment__image-tag">
                        <div class="col-12 mb-2">
                            <h5>Tag this file for a {{getUnitTypeForDisplay}}</h5>
                        </div>
                        <div class="col-12 mb-4">
                            <img v-if="isImage(taggingFile)" v-bind:src="taggingFile.url"/>
                        </div>
                        <div class="col-12 question__question capitalize">
                            {{getUnitTypeForDisplay}}:
                        </div>
                        <div class="col-12">
                            <select v-model="taggedForRoom" class="w-100 mb-4">
                                <option v-bind:value="null">None</option>
                                <option v-for="(roomType) of getRoomTypes"
                                        v-bind:key="roomType.reference"
                                        v-bind:value="roomType.reference">
                                    {{roomType.name}}
                                </option>
                            </select>
                        </div>
                        <div class="col-12 text-right">
                            <button v-on:click.stop="cancelTagging()" class="btn btn-secondary mr-2">Cancel</button>
                            <button v-on:click.stop="updateTag()" class="btn btn-primary" v-bind:disabled="taggingFile.roomType === taggedForRoom">Update</button>
                        </div>
                    </div>
                </div>
                <div v-show="!taggingFile" class="sw-modal-actions">
                    <div v-if="updating || error" class="infobar" v-bind:class="{'infobar-info' : !error, 'infobar-error' : error}">
                        {{infoMessage}}
                    </div>
                    <div v-show="updating && !success">
                        <Spinner/>
                    </div>
                    <div class="row" v-show="!updating && !success">
                        <div class="col text-center">
                            <button class="btn btn-primary" v-on:click="close">close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>

<script>
    import Vue from 'vue';

    import { getters } from '../../store.js'

    import Spinner from '../Spinner.vue'

    import { pageUtilitiesMixin } from '../../mixins/PageUtilitiesMixin.js';
    import { filesMixin } from '../../mixins/FilesMixin.js';

    const STATUS_INITIAL = 0, STATUS_SAVING = 1, STATUS_SUCCESS = 2, STATUS_FAILED = 3;

    export default {
        name: 'EditFiles',
        components: {
            Spinner
        },
        mixins: [pageUtilitiesMixin, filesMixin],
        props: {
            establishment: Object
        },
        data: function() {
            return {
                copy: {
                    preferences: {
                        correspondenceFromName: "",
                        correspondenceFromEmail: "",
                        displayCategoryForRoomTypes: "room",
                        colourPrimary: "",
                        colourSecondary: "",
                        files: [],
                        logo: null,
                    }
                },
                taggingFile: null,
                taggedForRoom: null,
                uploadError: null,
                currentStatus: null,
                deletingFileReference: null,
                updating: false,
                error: false,
                success: false,
                infoMessage: "",
                uploadFieldName: 'files',
                showTooltipPrimaryImage: false,
                showTooltipPageHeader: false
            }
        },
        computed: {
          isInitial() {
              return this.currentStatus === STATUS_INITIAL;
          },
          isSaving() {
              return this.currentStatus === STATUS_SAVING;
          },
          isSuccess() {
              return this.currentStatus === STATUS_SUCCESS;
          },
          isFailed() {
              return this.currentStatus === STATUS_FAILED;
          },
          getUnitTypeForDisplay: function() {
              return getters.getCurrentEstablishment().preferences.displayCategoryForRoomTypes;
          },
          getRoomTypes: function() {
              return getters.getCurrentEstablishment().roomTypes;
          }
        },
        methods: {
            isImage: function(file) {
                if (file.type && file.type.startsWith('image')) {
                    return true;
                } else {
                    return false;
                }
            },
            reset: function() {
                // reset form to initial state
                this.currentStatus = STATUS_INITIAL;
                this.infoMessage = null;
            },
            saveFiles: function(formData) {
                // upload data to the server
                this.currentStatus = STATUS_SAVING;

                this.$http.post('/guest-management/v1/establishments/' + this.establishment.reference + '/files/upload', formData)
                    .then(response => {
                        console.log("Files uploaded:");
                        console.log(response);
                        this.currentStatus = STATUS_SUCCESS;
                        const images = response.data;
                        this.establishment.preferences.files.push(...images);
                        for (let image of images) {
                            if (image.type && image.type.startsWith('image')) {
                                this.establishment.preferences.images.push(...images);
                            }
                        }
                        setTimeout(() => {
                            this.reset();
                        }, 3000);
                    }).catch(error => {
                        this.currentStatus = STATUS_FAILED;
                        if (error.response) {
                            if (error.response.status === 413) {
                                this.uploadError = "Error - file too large. Please try again."
                            } else {
                                this.uploadError = "Error: " + error.response.data.message
                            }
                        } else {
                            this.uploadError = "Error uploading images";
                        }
                    });
            },
            filesChange: function(fieldName, fileList) {
                // handle file changes
                const formData = new FormData();

                if (!fileList.length) {
                    return;
                }

                // append the files to FormData
                Array.from(Array(fileList.length).keys())
                     .map(x => {
                         formData.append(fieldName, fileList[x], fileList[x].name);
                     });

                // save it
                this.saveFiles(formData);
            },
            copyURL: async function(file, event) {
                try {
                    await this.copyToClipboard(file.url);

                    event.target.classList.add('edit-establishment__images-thumbnail-copy--copied');

                    setTimeout(() => {
                        event.target.classList.remove('edit-establishment__images-thumbnail-copy--copied');
                    }, 2000);

                } catch (err) {
                    console.error('Failed to copy URL:', err);
                }
            },
            deleteFile: function(reference) {
                this.error = false;
                console.log("Delete file with reference: " + reference);
                for (let index = 0; index < this.copy.preferences.files.length; index++) {
                    if (this.copy.preferences.files[index].reference === reference) {
                        this.deletingFileReference = reference;
                        this.$http.delete('/guest-management/v1/establishments/' + this.establishment.reference + '/files/' + reference
                        ).then(() => {
                            this.deletingFileReference = null;
                            this.copy.preferences.files.splice(index, 1);
                            for (let i = 0; i < this.copy.preferences.images.length; i++) {
                                if (this.copy.preferences.images[i].reference === reference) {
                                    this.copy.preferences.images.splice(i, 1);
                                    return;
                                }
                            }
                        }).catch(error => {
                            this.error = true;
                            this.deletingFileReference = null;
                            if (error.response) {
                                this.infoMessage = error.response.data.message
                            } else {
                                this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again"
                            }
                        });
                        return;
                    }
                }
            },
            isDeletingFile: function(reference) {
                return this.deletingFileReference && this.deletingFileReference === reference;
            },
            editFileTag: function(file) {
                this.taggingFile = file;
                this.taggedForRoom = file.roomType;
            },
            cancelTagging: function() {
                this.taggingFile = null;
            },
            updateTag: async function() {
                if (this.taggedForRoom) {
                    await this.tagFile();
                } else {
                    await this.deleteFileTag();
                }
                this.taggingFile = null;
                this.taggedForRoom = null;
            },
            tagFile: async function() {
                this.error = false;
                console.log("Tag file with reference: " + this.taggingFile.reference + " to room type: " + this.taggedForRoom);
                let body = {
                    "reference": this.taggedForRoom
                }
                for (let index = 0; index < this.copy.preferences.files.length; index++) {
                    if (this.copy.preferences.files[index].reference === this.taggingFile.reference) {
                        try {
                            let response = await this.$http.put('/guest-management/v1/establishments/' + this.establishment.reference + '/files/' + this.taggingFile.reference + '/tag', body);
                            console.log("File tagged");
                            if (response.data) {
                                Object.assign(this.taggingFile, response.data);
                            }
                            return;
                        } catch(error) {
                            this.error = true;
                            if (error.response) {
                                this.infoMessage = error.response.data.message
                            } else {
                                this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again"
                            }
                        }
                    }
                }
            },
            deleteFileTag: async function() {
                this.error = false;
                console.log("Deleting Tag for file with reference: " + this.taggingFile.reference);
                for (let index = 0; index < this.copy.preferences.files.length; index++) {
                    if (this.copy.preferences.files[index].reference === this.taggingFile.reference) {
                        try {
                            await this.$http.delete('/guest-management/v1/establishments/' + this.establishment.reference + '/files/' + this.taggingFile.reference + '/tag');
                            console.log("File tag deleted");
                            Vue.set(this.taggingFile, 'roomType', null);
                            return;
                        } catch(error) {
                            this.error = true;
                            if (error.response) {
                                this.infoMessage = error.response.data.message
                            } else {
                                this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again"
                            }
                        }
                    }
                }
            },
            close: function() {
                console.log("cancel");
                this.establishment.name = this.copy.name;
                this.$root.$emit('finished-editing-establishment-files');
            },
            save: function() {
                console.log("saving establishment");
                this.error = false;

                let body = {
                    "reference": this.copy.reference,
                    "preferences": {
                        "displayCategoryForRoomTypes": this.copy.preferences.displayCategoryForRoomTypes,
                        "showRoomTypeNameInsteadOfRoomName": this.copy.preferences.showRoomTypeNameInsteadOfRoomName,
                        "colourPrimary": this.copy.preferences.colourPrimary,
                        "colourSecondary": this.copy.preferences.colourSecondary,
                        "correspondenceFromName": this.copy.preferences.correspondenceFromName,
                        "correspondenceFromEmail": this.copy.preferences.correspondenceFromEmail,
                        "logo": this.copy.preferences.logo,
                        // this is here, so that we don't remove it by leaving it null
                        "frontDeskBackgroundImage": this.copy.preferences.frontDeskBackgroundImage,
                    }
                }

                // updating a form template
                this.updating = true;
                this.infoMessage = "Saving establishment";
                this.$http.put('/guest-management/v1/establishments/' + this.establishment.reference, body
                ).then(() => {
                    this.infoMessage = "Establishment updated";
                    this.success = true;
                    Object.assign(this.establishment, this.copy);
                    setTimeout(() => {
                        this.updating = false;
                        this.$root.$emit('finished-editing-establishment-appearance');
                    }, 2000);
                }).catch(error => {
                    this.error = true;
                    this.updating = false;
                    if (error.response) {
                        this.infoMessage = "Error: " + error.response.data.message
                    } else {
                        this.infoMessage = "Error updating establishment";
                    }
                });
            }
        },
        mounted() {
            this.reset();
            this.copy = { ...this.establishment };
            this.copy.preferences = { ...this.establishment.preferences };
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../styles/components/modal";
    @import "../../styles/components/edit_establishment";
    @import "../../styles/components/question_answer";
</style>